import React, { useState, useEffect } from 'react';
import { Shield, ArrowRight, Check, Eye, Sparkles, ChevronRight, Lock, BadgePercent, Ticket, Clock } from 'lucide-react';
import FallbackImage from '../../components/FallbackImage';

// Robust brand-logo chain (Wikimedia first — it reliably resolves; Clearbit/others as fallbacks).
const TARGET_LOGO_SRCS = [
    'https://upload.wikimedia.org/wikipedia/commons/c/c5/Target_Corporation_logo_%28vector%29.svg',
    'https://logo.clearbit.com/target.com',
    'https://1000logos.net/wp-content/uploads/2017/06/Target-Logo.png'
];

// Single demo card. The code below MUST stay in sync with the store checkout:
//   - code "4847-2951-0836-7294" normalizes to "4847295108367294", which the
//     StorePage demoCards map resolves to a $100.00 balance.
//   - value ($100) covers the entire demo cart ($97.96) for a clean full-coverage checkout.
const fakeCards = [
    {
        id: 'tutorial-card-1',
        retailer: 'Target',      // h3 below renders "Target Gift Card" -> extension parses retailer "Target"
        value: '100.00',
        pricePaid: '85.00',
        discount: 15,
        code: '4847-2951-0836-7294',
        pin: '8472',
        orderDate: 'Today',
        orderId: 'GCO-100482'
    }
];

// Visual representation of a real Target gift card.
const GiftCardArt = ({ card, isRevealed }) => (
    <div className="relative w-full rounded-2xl overflow-hidden shadow-card-prominent bg-gradient-to-br from-[#cc0000] via-[#c00000] to-[#990000] text-white aspect-[1.586/1]">
        {/* Decorative bullseye ring */}
        <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full border-[14px] border-white/10"></div>
        <div className="absolute -right-2 -bottom-12 w-40 h-40 rounded-full border-[14px] border-white/10"></div>

        <div className="relative h-full p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
                <div className="bg-white rounded-full p-1.5 shadow-md">
                    <FallbackImage srcs={TARGET_LOGO_SRCS} alt="Target" className="h-8 w-8 object-contain" />
                </div>
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/90">Gift Card</span>
            </div>

            <div>
                <p className="text-4xl sm:text-5xl font-extrabold font-display tracking-tight drop-shadow-sm">
                    ${card.value}
                </p>
                <p className="font-mono text-sm tracking-widest text-white/90 mt-1 card-code">
                    {isRevealed ? card.code : '•••• •••• •••• ••••'}
                </p>
            </div>
        </div>
    </div>
);

// Individual Demo Card Component
const DemoCard = ({ card, isRevealed, onReveal, isAdded }) => (
    // Root element keeps .tutorial-card-item for the extension parser — DO NOT RENAME
    <div className="tutorial-card-item group relative bg-white dark:bg-slate-800 rounded-2xl shadow-card overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-card-hover transition-all duration-300">

        {/* Order Header */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-start justify-between gap-3">
                <div>
                    {/* FIRST <h3> in the card — extension reads this as the retailer ("Target") */}
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white font-display leading-tight">
                        Target Gift Card
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Order #{card.orderId}</p>
                </div>
                <div className="flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full shadow-sm whitespace-nowrap">
                    <BadgePercent className="w-3.5 h-3.5" />
                    {card.discount}% OFF
                </div>
            </div>
        </div>

        {/* Card Body */}
        <div className="p-5">
            {/* The gift card itself */}
            <GiftCardArt card={card} isRevealed={isRevealed} />

            {/* Pricing line */}
            <div className="flex items-baseline justify-between mt-4">
                <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-slate-900 dark:text-white font-display">${card.value}</span>
                    <span className="text-sm text-slate-400 dark:text-slate-500">value</span>
                </div>
                <div className="text-right">
                    <span className="text-sm text-slate-400 dark:text-slate-500 line-through mr-1">${card.value}</span>
                    <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">you paid ${card.pricePaid}</span>
                </div>
            </div>

            {/* Card Details Section */}
            <div className={`mt-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 space-y-3 border ${isRevealed ? 'border-[#cc0000]/30 dark:border-[#cc0000]/40' : 'border-slate-200 dark:border-slate-700'} transition-colors`}>
                {/* PIN (card number lives in the art above as .card-code) */}
                <div>
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">PIN Code</p>
                    <div className="font-mono text-lg text-slate-800 dark:text-slate-200 card-pin">
                        {isRevealed ? card.pin : '••••'}
                    </div>
                </div>

                {/* Hidden fields for extension parser - DO NOT REMOVE */}
                <div className="hidden card-value">Value: ${card.value}</div>
                <div className="hidden card-price-paid">Price Paid: ${card.pricePaid}</div>
                <div className="hidden card-discount">Discount: {card.discount}%</div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-5 gap-3">
                {!isRevealed ? (
                    <button
                        onClick={() => onReveal(card.id)}
                        className="flex-1 flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-semibold py-3 px-4 rounded-xl transition-all duration-200 group"
                    >
                        <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        <span className="font-display">Reveal Card Details</span>
                    </button>
                ) : (
                    <div className="flex-1 flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 py-3 px-4 rounded-xl">
                        <Check className="w-4 h-4" />
                        <span className="font-medium text-sm">Card revealed</span>
                    </div>
                )}

                {/* Parser injection target for extension - DO NOT REMOVE */}
                <div className="tutorial-injection-target">
                    {isAdded && (
                        <div className="flex items-center gap-2 bg-brand-600 text-white py-3 px-4 rounded-xl">
                            <Check className="w-4 h-4" />
                            <span className="font-semibold font-display">Added</span>
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* Order Meta Footer */}
        <div className="bg-slate-50 dark:bg-slate-900/30 px-5 py-3 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Delivered {card.orderDate}
                </span>
                <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium">
                    <Shield className="w-3.5 h-3.5" />
                    Funds verified
                </span>
            </div>
        </div>
    </div>
);

// Progress Step Component
const ProgressStep = ({ number, title, isActive, isCompleted }) => (
    <div className={`flex items-center gap-3 ${isActive ? 'text-brand-700 dark:text-brand-400' : isCompleted ? 'text-brand-600 dark:text-brand-500' : 'text-slate-400 dark:text-slate-500'}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${isCompleted ? 'bg-brand-600 text-white' :
            isActive ? 'bg-brand-100 dark:bg-brand-900/50 text-brand-700 dark:text-brand-400 ring-2 ring-brand-500/50' :
                'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}>
            {isCompleted ? <Check className="w-4 h-4" /> : number}
        </div>
        <span className={`font-medium text-sm font-display ${isActive || isCompleted ? 'text-slate-900 dark:text-white' : ''}`}>
            {title}
        </span>
    </div>
);

export default function TutorialVendorPage() {
    const [revealedCards, setRevealedCards] = useState(new Set());
    const [addedCards, setAddedCards] = useState(new Set());
    const [currentStep, setCurrentStep] = useState(1);

    const handleReveal = (cardId) => {
        setRevealedCards(prev => new Set(prev).add(cardId));
        if (currentStep === 1) setCurrentStep(2);
    };

    // Listen for card additions from the extension
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'GIFT_SCOUT_CARD_ADDED') {
                setAddedCards(prev => new Set(prev).add(event.data.cardId));
                if (currentStep === 2) setCurrentStep(3);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [currentStep]);

    // Check if user is ready to proceed
    const canProceedToStore = addedCards.size > 0;

    return (
        <div className="bg-slate-50 dark:bg-slate-900 min-h-screen transition-colors duration-200">
            {/* Marketplace Storefront Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="container mx-auto max-w-6xl px-4">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-sm">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div className="leading-none">
                                <span className="font-extrabold text-lg text-slate-900 dark:text-white font-display">GiftCard Outlet</span>
                                <span className="block text-[11px] text-slate-400 dark:text-slate-500">Discount gift card marketplace</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <Lock className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="hidden sm:inline">Secure account</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Confirmation Banner */}
            <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 text-white">
                <div className="container mx-auto max-w-6xl px-4 py-8 sm:py-10">
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 w-fit mb-4">
                        <Sparkles className="w-4 h-4 text-yellow-300" />
                        <span className="text-xs font-medium">Interactive Demo · Your Orders</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-extrabold font-display mb-2">
                        Order confirmed — your gift card is ready
                    </h1>
                    <p className="text-indigo-100 max-w-2xl">
                        You just bought a discounted <strong>Target</strong> gift card. Save it to Gift Scout so it's
                        one tap away the next time you check out at Target.
                    </p>
                </div>
            </div>

            {/* Progress Tracker */}
            <div className="bg-white dark:bg-slate-800 shadow-sm border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                <div className="container mx-auto max-w-6xl px-4 py-4">
                    <div className="flex flex-wrap items-center justify-center lg:justify-between gap-4 lg:gap-8">
                        <ProgressStep number={1} title="Reveal the Card" isActive={currentStep === 1} isCompleted={revealedCards.size > 0} />
                        <ChevronRight className="w-5 h-5 text-slate-300 dark:text-slate-600 hidden lg:block" />
                        <ProgressStep number={2} title="Add to Scout" isActive={currentStep === 2} isCompleted={addedCards.size > 0} />
                        <ChevronRight className="w-5 h-5 text-slate-300 dark:text-slate-600 hidden lg:block" />
                        <ProgressStep number={3} title="Use at Checkout" isActive={currentStep === 3} isCompleted={false} />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto max-w-6xl px-4 py-8 sm:py-12">
                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Left: instructions */}
                    <div className="lg:col-span-3 order-2 lg:order-1">
                        {/* Instruction Card */}
                        <div className={`mb-6 p-5 sm:p-6 rounded-2xl border-l-4 transition-all duration-500 ${currentStep === 1
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-900 dark:text-blue-100'
                            : currentStep === 2
                                ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500 text-brand-900 dark:text-brand-100'
                                : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 text-emerald-900 dark:text-emerald-100'
                            }`}>
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${currentStep === 1 ? 'bg-blue-500' : currentStep === 2 ? 'bg-brand-500' : 'bg-emerald-500'
                                    }`}>
                                    {currentStep === 1 && <Eye className="w-5 h-5 text-white" />}
                                    {currentStep === 2 && <Ticket className="w-5 h-5 text-white" />}
                                    {currentStep === 3 && <Check className="w-5 h-5 text-white" />}
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1 font-display">
                                        {currentStep === 1 && "Step 1: Reveal your card details"}
                                        {currentStep === 2 && "Step 2: Save it to Gift Scout"}
                                        {currentStep === 3 && "Ready! Head to checkout"}
                                    </h3>
                                    <p className="text-sm opacity-90">
                                        {currentStep === 1 && "Click \"Reveal Card Details\" to see the full card number and PIN — just like you would in a real gift card order."}
                                        {currentStep === 2 && "Now click the green \"Add to Scout\" button that appeared on the card — this saves it securely to the extension."}
                                        {currentStep === 3 && "Your Target card is saved. Continue to the demo Target store to practice using it at checkout."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Why this works / trust strip */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 sm:p-6 shadow-card">
                            <h4 className="font-bold text-slate-900 dark:text-white font-display mb-3">Why save it to Gift Scout?</h4>
                            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                                <li className="flex items-start gap-3">
                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3" /></span>
                                    <span>No more digging through emails for card numbers at checkout.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3" /></span>
                                    <span>Gift Scout auto-fills the code and PIN on the store's payment page.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3" /></span>
                                    <span>Your remaining balance is tracked automatically after every purchase.</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Right: the card itself */}
                    <div className="lg:col-span-2 order-1 lg:order-2">
                        {/* Cards Grid - THIS ID IS CRITICAL for vendors.js / page-interactor.js */}
                        <div id="cards-container" className="grid gap-6">
                            {fakeCards.map(card => (
                                <DemoCard
                                    key={card.id}
                                    card={card}
                                    isRevealed={revealedCards.has(card.id)}
                                    onReveal={handleReveal}
                                    isAdded={addedCards.has(card.id)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Next Step CTA */}
                {canProceedToStore && (
                    <div className="mt-10 sm:mt-12 text-center animate-fade-in">
                        <div className="bg-gradient-to-r from-brand-700 to-brand-600 rounded-2xl p-8 sm:p-10 text-white shadow-card-prominent">
                            <div className="w-16 h-16 rounded-2xl bg-white/15 mx-auto mb-5 flex items-center justify-center">
                                <Check className="w-9 h-9 text-white" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-display">
                                Card saved to Gift Scout!
                            </h2>
                            <p className="text-brand-100 mb-6 max-w-lg mx-auto">
                                Now let's use your $100 Target gift card at checkout. Head to the demo Target store to finish the tutorial.
                            </p>
                            <a
                                href="/tutorial/store"
                                className="inline-flex items-center gap-2 bg-white text-brand-800 font-bold py-4 px-8 rounded-xl shadow-lg transition-all duration-200 hover:bg-brand-50 hover:scale-105 group font-display"
                            >
                                Continue to Target Checkout
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
